/**
 * Investor Profile API Service
 * Handles investor profile data via Supabase backend
 *
 * Phase 1 (single-source-of-truth): mock fallback REMOVED.
 * Returns { data: null } when API has no data; consumers render empty state.
 */

import { authFetch } from '../utils/authFetch';

class InvestorProfileAPI {
  constructor() {
    this.baseURL = import.meta.env.REACT_APP_API_URL || window.location.origin;
    this.apiPrefix = '/api/v1/investor';
  }

  /**
   * Make API request with error handling and optional abort signal
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Fetch options
   * @param {AbortSignal} options.signal - Optional abort signal for cancellation
   */
  async _fetch(endpoint, options = {}) {
    try {
      const { signal, ...fetchOptions } = options;

      const response = await authFetch(`${this.baseURL}${this.apiPrefix}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        },
        signal, // Pass abort signal to fetch
        ...fetchOptions,
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      // Don't log abort errors - they're expected
      if (error.name === 'AbortError') {
        throw error; // Re-throw so caller knows it was cancelled
      }
      console.warn(`API call failed: ${endpoint}`, error);
      return null;
    }
  }

  /**
   * Write (POST/PATCH) helper that CLASSIFIES the failure so the caller's
   * auto-sync retry loop can tell TRANSIENT failures (network, 408/429, 5xx,
   * and 401 — the Clerk token may refresh on retry) from PERMANENT ones (403
   * ownership, 400/422 validation — retrying just fails again). Unlike `_fetch`,
   * which collapses every error to `null`, this preserves the error type.
   * @returns {Promise<{success:boolean, data?:any, savedAt?:string, error?:string, transient?:boolean, status?:number}>}
   */
  async _write(endpoint, { method, body, signal } = {}) {
    let response;
    try {
      response = await authFetch(`${this.baseURL}${this.apiPrefix}${endpoint}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      });
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      // Network failure (offline, DNS, TLS, timeout) — always worth a retry.
      return { success: false, error: 'Network error — retrying', transient: true };
    }

    if (response.ok) {
      const json = await response.json().catch(() => null);
      // A 200 that still carries success:false is an app-level decline — permanent.
      if (json && json.success === false) {
        return { success: false, error: json.error || 'Save was rejected', transient: false, status: response.status };
      }
      return {
        success: true,
        data: json?.data ?? json,
        savedAt: json?.data?.updated_at || json?.updated_at || new Date().toISOString(),
      };
    }

    const status = response.status;
    const transient = status === 401 || status === 408 || status === 429 || status >= 500;
    let error;
    if (status === 403) error = "You don't have permission to save this profile";
    else if (status === 400 || status === 422) error = 'Some of your data was rejected — please review and try again';
    else if (status === 401) error = 'Reconnecting…';
    else if (transient) error = 'Server is busy — retrying';
    else error = `Save failed (${status})`;
    return { success: false, error, transient, status };
  }

  /**
   * Get investor profile by user ID.
   * Returns { data: null } when API has no data — consumers render empty state.
   * @param {string} userId - User ID
   * @param {Object} options - Options including optional abort signal
   * @param {AbortSignal} options.signal - Optional abort signal for cancellation
   * @param {boolean} options.skipMockFallback - DEPRECATED no-op (kept for caller compatibility); behavior is now always "no mock fallback".
   */
  async getInvestorProfile(userId, options = {}) {
    if (!userId) throw new Error('getInvestorProfile: userId required');
    const { signal } = options;

    try {
      const result = await this._fetch(`/profile/${userId}`, { signal });

      if (result?.success && result.data) {
        return {
          success: true,
          data: this._transformApiToProfile(result.data),
          rawData: result.data, // raw backend shape — portfolio_data/strategy_data intact
          source: 'api'
        };
      }

      // No data — return null, never substitute fake data
      return {
        success: true,
        data: null,
        source: 'none',
        fallbackReason: 'no_data'
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }

      console.error('Error fetching investor profile:', error);

      return {
        success: false,
        data: null,
        source: 'none',
        error: error.message
      };
    }
  }

  /**
   * Get portfolio strategy by user ID
   * @param {string} userId - User ID
   * @param {Object} options - Options including optional abort signal
   * @param {AbortSignal} options.signal - Optional abort signal for cancellation
   */
  async getPortfolioStrategy(userId, options = {}) {
    if (!userId) throw new Error('getPortfolioStrategy: userId required');
    const { signal } = options;

    try {
      const result = await this._fetch(`/profile/${userId}`, { signal });

      if (result?.success && result.data?.strategy_data) {
        return {
          success: true,
          data: result.data.strategy_data,
          source: 'api'
        };
      }

      return {
        success: true,
        data: null,
        source: 'none',
        fallbackReason: 'no_data'
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }

      return {
        success: false,
        data: null,
        source: 'none',
        error: error.message
      };
    }
  }

  /**
   * Patch portfolio data only (efficient partial update)
   * Used by "Save to Cloud" feature
   */
  async patchPortfolioData(userId, portfolioData) {
    // Goes through _write so failures carry a transient/permanent classification
    // (used by the auto-sync retry loop) instead of collapsing to a generic error.
    return this._write(`/profile/${userId}/portfolio`, { method: 'PATCH', body: portfolioData });
  }

  /**
   * Update investor profile (LEGACY — runs through _transformProfileToApi)
   */
  async updateInvestorProfile(userId, profileData) {
    try {
      const apiData = this._transformProfileToApi(profileData);

      // Older portfolio editors do not carry property features. This endpoint
      // replaces JSONB columns, so omission must preserve the saved rules.
      // An explicit {} in either supported input shape still clears them.
      if (profileData.propertyFeatures === undefined && profileData.preferences_data?.propertyFeatures === undefined) {
        const current = await this._fetch(`/profile/${userId}`);
        if (!current?.success || !current.data) {
          return { success: false, error: 'Couldn’t load your saved property preferences. Nothing was saved. Try again.' };
        }
        const propertyFeatures = current.data.preferences_data?.propertyFeatures
          ?? current.data.profile_data?.propertyFeatures ?? {};
        apiData.preferences_data.propertyFeatures = propertyFeatures;
        apiData.profile_data.propertyFeatures = propertyFeatures;
      }

      const result = await this._fetch(`/profile/${userId}`, {
        method: 'POST',
        body: JSON.stringify(apiData),
      });

      if (result?.success) {
        return {
          success: true,
          data: this._transformApiToProfile(result.data),
          message: 'Profile updated successfully'
        };
      }

      throw new Error(result?.error || 'Failed to update profile');
    } catch (error) {
      console.error('Error updating profile:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Upsert the full investor profile in pre-built backend format (Phase 1).
   *
   * Used by PortfolioDataContext's silent auto-sync. Bypasses _transformProfileToApi
   * which would drop the canonical v6 fields (householdIncome, householdExpenses,
   * geographicPreferences, etc). Caller MUST pass the exact backend shape:
   *   { entity_type, risk_profile, profile_data, portfolio_data, strategy_data, preferences_data }
   */
  async upsertFullProfile(userId, fullPayload) {
    // Goes through _write so the auto-sync retry loop can tell transient failures
    // (worth retrying) from permanent ones (auth/validation — retrying won't help).
    return this._write(`/profile/${userId}`, { method: 'POST', body: fullPayload });
  }

  /**
   * Update portfolio strategy
   */
  async updatePortfolioStrategy(userId, strategyData) {
    try {
      // Get current profile first
      const currentResult = await this._fetch(`/profile/${userId}`);
      const currentData = currentResult?.data || {};

      const result = await this._fetch(`/profile/${userId}`, {
        method: 'POST',
        body: JSON.stringify({
          ...currentData,
          strategy_data: strategyData
        }),
      });

      if (result?.success) {
        return {
          success: true,
          data: result.data.strategy_data,
          message: 'Strategy updated successfully'
        };
      }

      throw new Error(result?.error || 'Failed to update strategy');
    } catch (error) {
      console.error('Error updating strategy:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get investor profile summary for quick display
   */
  async getInvestorSummary(userId) {
    if (!userId) throw new Error('getInvestorSummary: userId required');
    try {
      const profileResult = await this.getInvestorProfile(userId);
      const strategyResult = await this.getPortfolioStrategy(userId);

      const profile = profileResult.data;
      const strategy = strategyResult.data;

      return {
        success: true,
        data: {
          name: profile.name || profile.profile_data?.name,
          entityType: profile.investmentEntity?.type || profile.entity_type,
          riskProfile: profile.riskProfile || profile.risk_profile,
          portfolioValue: profile.currentPortfolio?.totalValue || profile.portfolio_data?.totalValue,
          portfolioEquity: profile.currentPortfolio?.totalEquity || profile.portfolio_data?.totalEquity,
          propertyCount: profile.currentPortfolio?.properties?.length || profile.portfolio_data?.properties?.length || 0,
          targetYield: strategy.targetReturns?.grossYield,
          maxLVR: strategy.gearing?.maxLVR
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get portfolio context for Deal Copilot RAG
   * Returns null gracefully if no portfolio
   */
  async getPortfolioContext(userId) {
    if (!userId) throw new Error('getPortfolioContext: userId required');
    try {
      const result = await this._fetch(`/profile/${userId}/context`);
      return result || { has_portfolio: false };
    } catch (error) {
      return { has_portfolio: false };
    }
  }

  /**
   * Get verified buyer badge status
   */
  async getVerifiedBuyerBadge(userId) {
    if (!userId) throw new Error('getVerifiedBuyerBadge: userId required');
    try {
      const result = await this._fetch(`/profile/${userId}/badge`);
      return result || { badge: 'registered', finance_expires_at: null };
    } catch (error) {
      return { badge: 'registered', finance_expires_at: null };
    }
  }

  /**
   * Get pre-approval status with full details
   */
  async getPreApprovalStatus(userId) {
    if (!userId) throw new Error('getPreApprovalStatus: userId required');
    try {
      const result = await this._fetch(`/profile/${userId}/preapproval`);
      return result || { has_preapproval: false, badge: 'registered' };
    } catch (error) {
      return { has_preapproval: false, badge: 'registered' };
    }
  }

  /**
   * Upload pre-approval PDF for verification
   * Returns extracted finance details and updates badge status
   */
  async uploadPreApproval(userId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authFetch(
        `${this.baseURL}${this.apiPrefix}/profile/${userId}/preapproval`,
        {
          method: 'POST',
          body: formData,
          // Don't set Content-Type header - browser will set it with boundary
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Pre-approval upload error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ============================================================================
  // DATA SEEDING
  // ============================================================================

  /**
   * Seed the database with demo investor data
   * Call this to populate the DB with the mock data structure
   */
  async seedDemoData() {
    try {
      const response = await authFetch(
        `${this.baseURL}${this.apiPrefix}/seed-demo-data`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(`Seed failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('Demo data seeded:', result);
      return result;
    } catch (error) {
      console.error('Error seeding demo data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if profile exists in database (not just mock fallback)
   */
  async hasPersistedProfile(userId) {
    if (!userId) throw new Error('hasPersistedProfile: userId required');
    try {
      const result = await this._fetch(`/profile/${userId}`);
      // Check if we got real data (not empty shell)
      return result?.success &&
             result?.data?.portfolio_data?.properties?.length > 0;
    } catch {
      return false;
    }
  }

  // ============================================================================
  // PHASE 5: PORTFOLIO RETURN LOOP
  // ============================================================================

  /**
   * Add a settled property to portfolio
   * @param {string} userId - User ID
   * @param {Object} propertyData - Property details from settled deal
   */
  async addSettledPropertyToPortfolio(userId, propertyData) {
    try {
      const response = await authFetch(
        `${this.baseURL}${this.apiPrefix}/profile/${userId}/portfolio/property`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(propertyData),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to add property: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding property to portfolio:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get portfolio property by deal ID
   * @param {string} userId - User ID
   * @param {string} dealId - Deal ID to check
   */
  async getPortfolioPropertyByDeal(userId, dealId) {
    try {
      const result = await this._fetch(`/profile/${userId}/portfolio/property/${dealId}`);
      return result || { in_portfolio: false };
    } catch (error) {
      return { in_portfolio: false };
    }
  }

  /**
   * Update a portfolio property (rent, value, etc.)
   * @param {string} userId - User ID
   * @param {string} propertyId - Property ID in portfolio
   * @param {Object} updates - Fields to update
   */
  async updatePortfolioProperty(userId, propertyId, updates) {
    try {
      const response = await authFetch(
        `${this.baseURL}${this.apiPrefix}/profile/${userId}/portfolio/property/${propertyId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update property: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating portfolio property:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate portfolio fit score for a property
   */
  async calculatePortfolioFit(userId, propertyData) {
    try {
      const profileResult = await this.getInvestorProfile(userId);
      const strategyResult = await this.getPortfolioStrategy(userId);

      if (!profileResult.success || !strategyResult.success) {
        return { success: false, error: 'Profile or strategy not found' };
      }

      const profile = profileResult.data;
      const strategy = strategyResult.data;

      // Calculate fit score
      const fitScore = this._calculateFitScore(profile, strategy, propertyData);

      return {
        success: true,
        data: fitScore
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ============================================================================
  // TRANSFORM HELPERS
  // ============================================================================

  /**
   * Transform API response to legacy profile format
   */
  _transformApiToProfile(apiData) {
    if (!apiData) return null;

    // If it's already in legacy format (from mock), return as-is
    if (apiData.investmentEntity) return apiData;

    const profileData = apiData.profile_data || {};
    const portfolioData = apiData.portfolio_data || {};
    const preferencesData = apiData.preferences_data || {};

    return {
      id: apiData.user_id,
      name: profileData.name,
      email: profileData.email,
      investmentEntity: profileData.investmentEntity || {
        type: apiData.entity_type,
        name: profileData.entityName,
        abn: profileData.abn
      },
      riskProfile: apiData.risk_profile || profileData.riskProfile,
      investmentHorizon: profileData.investmentHorizon,
      age: profileData.age,
      stateOfResidence: profileData.stateOfResidence,
      householdIncome: profileData.householdIncome || {},
      householdExpenses: profileData.householdExpenses || {},
      taxPosition: profileData.taxPosition || {},
      assets: profileData.assets || {},
      preferredAssetClasses: preferencesData.preferredAssetClasses || {},
      geographicPreferences: preferencesData.geographicPreferences || profileData.geographicPreferences || {},
      propertyFeatures: preferencesData.propertyFeatures ?? profileData.propertyFeatures ?? {},
      currentPortfolio: {
        totalValue: portfolioData.totalValue,
        totalEquity: portfolioData.totalEquity,
        properties: portfolioData.properties || []
      },
      liquidityRequirements: profileData.liquidityRequirements || {},
      firbStatus: profileData.firbStatus || {},
      verifiedBuyerBadge: apiData.verified_buyer_badge,
      financeExpiresAt: apiData.finance_expires_at
    };
  }

  /**
   * Transform legacy profile format to API format
   */
  _transformProfileToApi(profile) {
    return {
      entity_type: profile.investmentEntity?.type || profile.entity_type,
      risk_profile: profile.riskProfile || profile.risk_profile,
      profile_data: {
        ...profile, // Preserve all canonical fields
        name: profile.name,
        email: profile.email,
        investmentEntity: profile.investmentEntity,
        investmentHorizon: profile.investmentHorizon,
        age: profile.age,
        stateOfResidence: profile.stateOfResidence,
        householdIncome: profile.householdIncome,
        householdExpenses: profile.householdExpenses,
        taxPosition: profile.taxPosition,
        assets: profile.assets,
        geographicPreferences: profile.geographicPreferences,
        liquidityRequirements: profile.liquidityRequirements,
        firbStatus: profile.firbStatus
      },
      portfolio_data: profile.currentPortfolio || profile.portfolio_data || {},
      strategy_data: profile.strategy_data || {},
      preferences_data: {
        preferredAssetClasses: profile.preferredAssetClasses,
        geographicPreferences: profile.geographicPreferences,
        propertyFeatures: profile.propertyFeatures ?? profile.preferences_data?.propertyFeatures ?? {},
      }
    };
  }

  // ============================================================================
  // FIT SCORE CALCULATION (kept on frontend for now)
  // ============================================================================

  _calculateFitScore(profile, strategy, property) {
    let totalScore = 0;
    const breakdown = {};

    // Geographic fit (20 points)
    const geoPrefs = profile.geographicPreferences || profile.preferences_data?.geographicPreferences;
    if (geoPrefs) {
      const geoFit = this._calculateGeographicFit(geoPrefs, property);
      breakdown.geographic = geoFit;
      totalScore += geoFit.score;
    }

    // Yield fit (25 points)
    const targetReturns = strategy.targetReturns || strategy.targetReturns;
    if (targetReturns) {
      const yieldFit = this._calculateYieldFit(targetReturns, property);
      breakdown.yield = yieldFit;
      totalScore += yieldFit.score;
    }

    // Asset class fit (15 points)
    const assetPrefs = profile.preferredAssetClasses || profile.preferences_data?.preferredAssetClasses;
    if (assetPrefs) {
      const assetFit = this._calculateAssetClassFit(assetPrefs, property);
      breakdown.assetClass = assetFit;
      totalScore += assetFit.score;
    }

    // Deal size fit (15 points)
    const dealSizeRange = strategy.dealSizeRange;
    if (dealSizeRange) {
      const sizeFit = this._calculateDealSizeFit(dealSizeRange, property);
      breakdown.dealSize = sizeFit;
      totalScore += sizeFit.score;
    }

    // Diversification impact (15 points)
    const portfolio = profile.currentPortfolio || profile.portfolio_data;
    const divRules = strategy.diversificationRules;
    if (portfolio && divRules) {
      const diversificationFit = this._calculateDiversificationFit(portfolio, divRules, property);
      breakdown.diversification = diversificationFit;
      totalScore += diversificationFit.score;
    }

    // Risk fit (10 points)
    const riskProfile = profile.riskProfile || profile.risk_profile;
    if (riskProfile) {
      const riskFit = this._calculateRiskFit(riskProfile, property);
      breakdown.risk = riskFit;
      totalScore += riskFit.score;
    }

    return {
      overallScore: Math.round(totalScore),
      breakdown,
      recommendation: this._getRecommendation(totalScore),
      keyStrengths: this._getKeyStrengths(breakdown),
      concerns: this._getConcerns(breakdown)
    };
  }

  _calculateGeographicFit(preferences, property) {
    const states = preferences.states || [];
    const state = property.state || this._extractState(property.address);
    const isPreferredState = states.includes(state);

    return {
      score: isPreferredState ? 20 : 5,
      reason: isPreferredState ? "Matches preferred state" : "Outside preferred states",
      details: { preferredStates: states, propertyState: state }
    };
  }

  _calculateYieldFit(targetReturns, property) {
    const propertyYield = property.grossYield || (property.weeklyRent * 52 / property.currentValue * 100);
    const targetYield = targetReturns.grossYield || 5;
    const difference = propertyYield - targetYield;

    let score = 25;
    if (difference >= 0) {
      score = Math.min(25, 15 + (difference * 2));
    } else {
      score = Math.max(0, 15 + (difference * 3));
    }

    return {
      score: Math.round(score),
      reason: difference >= 0 ? "Meets or exceeds yield target" : "Below yield target",
      details: { propertyYield: propertyYield?.toFixed(2), targetYield, difference: difference?.toFixed(2) }
    };
  }

  _calculateAssetClassFit(preferences, property) {
    const propertyType = property.type?.toLowerCase() || 'residential';
    const assetClass = this._mapPropertyTypeToAssetClass(propertyType);
    const preference = preferences[assetClass];

    if (!preference || !preference.enabled) {
      return {
        score: 0,
        reason: "Asset class not in preferences",
        details: { propertyType, assetClass, enabled: false }
      };
    }

    return {
      score: 15,
      reason: "Matches preferred asset class",
      details: { propertyType, assetClass, allocation: preference.allocation }
    };
  }

  _calculateDealSizeFit(dealSizeRange, property) {
    const propertyValue = property.currentValue || property.purchasePrice || 0;
    const { minimum, maximum, preferred } = dealSizeRange;

    if (propertyValue < minimum || propertyValue > maximum) {
      return {
        score: 0,
        reason: "Outside deal size range",
        details: { propertyValue, minimum, maximum }
      };
    }

    const distanceFromPreferred = Math.abs(propertyValue - preferred) / preferred;
    const score = Math.max(5, 15 - (distanceFromPreferred * 10));

    return {
      score: Math.round(score),
      reason: "Within deal size range",
      details: { propertyValue, preferred, distanceFromPreferred }
    };
  }

  _calculateDiversificationFit(currentPortfolio, rules, property) {
    const properties = currentPortfolio.properties || [];
    const propertyCount = properties.length;
    const hasMinProperties = propertyCount >= (rules.minNumberOfProperties || 3);

    return {
      score: hasMinProperties ? 15 : 10,
      reason: hasMinProperties ? "Good portfolio diversification" : "Building diversification",
      details: { currentProperties: propertyCount, minimum: rules.minNumberOfProperties }
    };
  }

  _calculateRiskFit(riskProfile, property) {
    const riskScores = { Conservative: 5, Balanced: 8, Growth: 10, Aggressive: 10 };
    return {
      score: riskScores[riskProfile] || 5,
      reason: `Aligns with ${riskProfile} risk profile`,
      details: { riskProfile }
    };
  }

  _mapPropertyTypeToAssetClass(propertyType) {
    const mapping = {
      'house': 'residential',
      'apartment': 'residential',
      'townhouse': 'residential',
      'unit': 'residential',
      'office': 'commercial',
      'retail': 'retail',
      'warehouse': 'industrial',
      'factory': 'industrial',
      'mixed': 'mixedUse'
    };
    return mapping[propertyType] || 'residential';
  }

  _extractState(address) {
    if (!address) return null;
    const states = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
    const upper = address.toUpperCase();
    for (const state of states) {
      if (upper.includes(state)) return state;
    }
    return null;
  }

  _getRecommendation(score) {
    if (score >= 80) return "Strong Fit - Highly Recommended";
    if (score >= 60) return "Good Fit - Recommended";
    if (score >= 40) return "Moderate Fit - Consider";
    return "Poor Fit - Not Recommended";
  }

  _getKeyStrengths(breakdown) {
    return Object.entries(breakdown)
      .filter(([_, data]) => data.score >= 15)
      .map(([key, data]) => ({ category: key, reason: data.reason }));
  }

  _getConcerns(breakdown) {
    return Object.entries(breakdown)
      .filter(([_, data]) => data.score < 10)
      .map(([key, data]) => ({ category: key, reason: data.reason }));
  }
}

// Export singleton instance
export const investorProfileAPI = new InvestorProfileAPI();
export default investorProfileAPI;
